import { useEffect, useState, useContext, useCallback } from 'react';
import { Save, Building2 } from 'lucide-react';
import { getMy, updateMy } from '@/services/companies.js';
import UserContext from '../../context/users';
import { AdminRole } from '@/constants.js';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ImageUpload } from '@/components/ui/image-upload';
import { cn } from '@/lib/cn';

function Skeleton({ className, style }) {
    return (
        <div
            className={cn('animate-pulse rounded-xl', className)}
            style={{ background: 'var(--dash-surface-hover)', ...style }}
        />
    );
}

function SettingsSkeleton() {
    return (
        <Card>
            <CardContent className="p-6">
                <div className="flex flex-col gap-6 lg:flex-row lg:gap-12">
                    <div className="flex flex-1 flex-col gap-5">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-11 w-full" />
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-11 w-full" />
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="h-11 w-32" />
                        <Skeleton className="h-10 w-36 !rounded-xl" />
                    </div>
                    <div className="flex flex-col items-center gap-3">
                        <Skeleton className="h-24 w-24 !rounded-2xl" />
                        <Skeleton className="h-3 w-20" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function CompanySettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [currentUser] = useContext(UserContext);

    const [name, setName] = useState('');
    const [identity, setIdentity] = useState('');
    const [payDay, setPayDay] = useState('');
    const [logoImage, setLogoImage] = useState(null);

    useEffect(() => {
        setLoading(true);
        getMy()
            .then(({ data }) => {
                setName(data.name || '');
                setIdentity(data.identity || '');
                setPayDay(data.payDay || '');
                setLogoImage(data.logoImage || null);
            })
            .catch((e) => {
                console.error(e);
                toast.error('Falha ao carregar informações da empresa');
            })
            .finally(() => setLoading(false));
    }, []);

    const onSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            if (!name.trim()) {
                toast.error('Nome da empresa é obrigatório');
                return;
            }
            setSaving(true);
            try {
                await updateMy({ name, identity, logoImage });
                toast.success('Empresa atualizada com sucesso!');
            } catch (err) {
                console.error(err);
                toast.error('Falha ao atualizar empresa');
            } finally {
                setSaving(false);
            }
        },
        [name, identity, logoImage]
    );

    if (loading) return <SettingsSkeleton />;

    const isAdmin = currentUser?.role === AdminRole;

    return (
        <Card>
            <CardContent className="p-5 sm:p-6">
                <form onSubmit={onSubmit}>
                    <div className="flex flex-col gap-8 lg:flex-row lg:gap-12">
                        {/* Fields */}
                        <div className="flex flex-1 flex-col gap-5">
                            {/* Name */}
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="companyName" required>
                                    Nome da Empresa
                                </Label>
                                <Input
                                    id="companyName"
                                    leftIcon={Building2}
                                    placeholder="Ex: Minha Empresa Ltda"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>

                            {/* Identity */}
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="identity">CNPJ / Documento</Label>
                                <Input
                                    id="identity"
                                    placeholder="00000000000000"
                                    value={identity}
                                    onChange={(e) => setIdentity(e.target.value)}
                                    maxLength={14}
                                />
                                <p className="text-[11px] leading-relaxed" style={{ color: 'var(--dash-text-faint)' }}>
                                    Utilizado para emissão de notas fiscais e faturas.
                                </p>
                            </div>

                            {/* Pay Day */}
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="payDay" required>
                                    Dia de Pagamento
                                </Label>
                                <Input
                                    id="payDay"
                                    type="number"
                                    min={1}
                                    max={28}
                                    placeholder="1 - 28"
                                    value={payDay}
                                    onChange={(e) => setPayDay(e.target.value)}
                                    disabled={!isAdmin}
                                    className="max-w-[160px]"
                                />
                                {!isAdmin && (
                                    <p className="text-[11px]" style={{ color: 'var(--dash-text-faint)' }}>
                                        Apenas administradores podem alterar.
                                    </p>
                                )}
                            </div>

                            {/* Submit */}
                            <div className="pt-2">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    leftIcon={Save}
                                    loading={saving}
                                >
                                    Salvar Alterações
                                </Button>
                            </div>
                        </div>

                        {/* Logo */}
                        <div className="flex flex-col items-center gap-3">
                            <Label>Logo da Empresa</Label>
                            <ImageUpload
                                value={logoImage}
                                onChange={setLogoImage}
                                shape="rounded"
                                size="lg"
                                placeholder="Clique para enviar"
                            />
                        </div>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}
